import React from "react";
import GoogleMapReact, {
  // MapOptions,
  // fitBounds,
  Props as MapProps,
} from "google-map-react";
import { useEffect, useMemo, useState } from "react";
import { REACT_APP_MAPS_API_KEY } from "config";

// import { MapMarker } from "./MapMarker";
import PinMarker from "./PinMarker";

const googleMapApiKey = REACT_APP_MAPS_API_KEY;

export const MAP_STYLES = [
  {
    featureType: "poi.business",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.arterial",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road.local",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
];

export type MapData = {
  location: { lat: number; lng: number };
  label?: string;
  id: number;
};

type Props = {
  data: MapData[];
  selectedId?: number;
  handleMarkerSelected?: (id: number) => void;
  onDrag?: () => void;
  onZoomAnimationStart?: () => void;
  markerType?: "price" | "pin";
  withBounds?: boolean;
  view?: boolean;
};

const DEFAULT_ZOOM = 15; // Between 1 and 20;

export const Map: React.FC<Props> = ({
  data,
  selectedId,
  handleMarkerSelected = (id: any) => {},
  onZoomAnimationStart,
  onDrag,
  markerType = "price",
  withBounds = false,
  view,
}) => {
  const [map, setMap] = useState<any>(null);
  const [maps, setMaps] = useState<any>(null);
  const [mapOptions, setMapOptions] = useState<GoogleMapReact.MapOptions>({
    styles: MAP_STYLES,
    fullscreenControl: false,
    zoomControl: false,
    maxZoom: DEFAULT_ZOOM, // This is the default zoom when the map loads, we remove this restiction in the onTilesLoaded() function so the user can scroll the full range again
    // @ts-ignore
    options: { gestureHandling: "greedy" },
    streetViewControl: true,
  });

  const defaultProps: MapProps = useMemo(
    () => ({
      center: {
        ...(data[0]?.location ?? {
          lat: 51.381871,
          lng: -2.360524,
        }),
      },
      zoom: DEFAULT_ZOOM,
    }),
    []
  );

  const [center, setCenter] = useState(defaultProps.center);

  useEffect(() => {
    if (data && data[0]?.location) setCenter(data[0].location);
  }, [data]);

  const fitBounds = (map: any, maps: any) => {
    if (!maps || !map) return;

    const bounds = new maps.LatLngBounds();
    data.forEach(item => {
      const latLng = new maps.LatLng(item.location.lat, item.location.lng);
      bounds.extend(latLng);
    });
    map.fitBounds(bounds);
  };

  useEffect(() => {
    if (!maps || !map || !withBounds) return;

    fitBounds(map, maps);
  }, [data]);

  const handleApiLoaded = (map: any, maps: any) => {
    if (!withBounds) return;

    let tempMap = map;

    if (tempMap.zoom && defaultProps.zoom) {
      tempMap.zoom = defaultProps.zoom;
    }

    setMap(tempMap);

    setMaps(maps);
    fitBounds(tempMap, maps);
  };

  return (
    // NOTE: Requires specified height
    <div className={`MapContainer ${view ? "MapBorder" : ""}`}>
      {googleMapApiKey && mapOptions && (
        <GoogleMapReact
          bootstrapURLKeys={{
            key: googleMapApiKey as string,
          }}
          onTilesLoaded={() => setMapOptions({ ...mapOptions, maxZoom: 20 })} // 20 is the maxmimum zoom the package allows, so we lift the zoom restiction set in mapOptions by default so the map loads with the desired zoom level of 15
          resetBoundsOnResize={true}
          defaultCenter={defaultProps.center}
          defaultZoom={DEFAULT_ZOOM}
          center={center}
          options={mapOptions}
          onDrag={onDrag}
          onZoomAnimationStart={onZoomAnimationStart}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        >
          {data.map((item, i) => {
            return <PinMarker key={i} {...item.location} label={item.label} />;
          })}
        </GoogleMapReact>
      )}
    </div>
  );
};

export default Map;
